import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { PROVIDER } from '../../util/types';
import {
  VALID_RESERVATION_TRANSITIONS,
  INQUIRE_TRANSITION,
  resolveBookingStart,
  indexIncludedBookings,
  queryAllTransactionPages,
  isBookingInPast,
  shouldListInquiry,
} from '../../util/bookingTimeline';

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/PastListingsPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/PastListingsPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/PastListingsPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(payload.data.data),
        pagination: payload.data.meta,
      };
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const userMaybe = getState().user.currentUser;
  const userReady = userMaybe ? Promise.resolve() : dispatch(fetchCurrentUser());

  return userReady.then(() => {
    const user = getState().user.currentUser;
    const userType = user && user.id && user.attributes.profile.publicData.userType;
    const isProvider = userType == PROVIDER;

    const onlyFilterValues = {
      orders: 'order',
      sales: 'sale',
    };

    const requiredTab = isProvider ? 'sales' : 'orders';

    const onlyFilter = onlyFilterValues[requiredTab];
    if (!onlyFilter) {
      return Promise.reject(new Error(`Invalid tab for PastListingsPage: ${requiredTab}`));
    }

    dispatch(fetchOrdersOrSalesRequest());

    const apiQueryParams = {
      only: onlyFilter,
      lastTransitions: VALID_RESERVATION_TRANSITIONS,
      include: [
        'listing',
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
      ],
      'fields.transaction': [
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
        'metadata',
        'protectedData',
      ],
      'fields.listing': [
        'title',
        'availabilityPlan',
        'publicData.listingType',
        // Para mostrar el título de la cara reservada (`bookedFaceView`).
        'publicData.gardenTitle',
        'publicData.searchListingType',
        'publicData.category',
      ],
      'fields.booking': ['start', 'end', 'displayStart', 'displayEnd', 'state'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      sort: '-bookingStart',
    };

    const queryOpts = { expand: true };

    // Keep only reservations whose booking date has already passed. This mirrors
    // UpcomingListingsPage (which keeps the future ones) so that accepted-but-not-
    // yet-completed reservations no longer fall through the gap between the two pages.
    const filterUpcomingBookings = response => {
      const raw = response.data.data || [];
      const includedBookingsByUuid = indexIncludedBookings(response);
      const now = new Date();
      const data = raw
        .filter(rawTx => {
          const isInquire = rawTx?.attributes?.lastTransition === INQUIRE_TRANSITION;
          if (isInquire) {
            return (
              shouldListInquiry(rawTx?.attributes?.metadata) &&
              isBookingInPast(rawTx, includedBookingsByUuid, now)
            );
          }
          return isBookingInPast(rawTx, includedBookingsByUuid, now);
        })
        .sort((a, b) => {
          // Most recently-ended reservations first.
          const startA = resolveBookingStart(a, includedBookingsByUuid);
          const startB = resolveBookingStart(b, includedBookingsByUuid);
          if (!startA && !startB) return 0;
          if (!startA) return 1;
          if (!startB) return -1;
          return startB.getTime() - startA.getTime();
        });
      return {
        data: {
          data,
          included: response.data.included,
          meta: { ...response.data.meta, totalItems: data.length },
        },
      };
    };

    const runSuccess = response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    };

    return queryAllTransactionPages(sdk, apiQueryParams, queryOpts)
      .then(response => runSuccess(filterUpcomingBookings(response)))
      .catch(e => {
        dispatch(fetchOrdersOrSalesError(storableError(e)));
        throw e;
      });
  });
};
